import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import weekday from "dayjs/plugin/weekday";
import type { ITimerCountDown } from "../node/type";
import { isNotFalsy } from "./value";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(weekday);

export const setRAFInterval = (callback: () => void, duration: number) => {
  let _startTime: number | null = null;
  let _stopped = false;

  const action = (timestamp: number) => {
    if (!_startTime) {
      _startTime = timestamp;
    }

    const elapsed = timestamp - _startTime;
    if (elapsed >= duration) {
      callback();
      _startTime = null;
    }

    if (!_stopped) {
      requestAnimationFrame(action);
    }
  };

  requestAnimationFrame(action);

  return () => {
    _stopped = true;
  };
};

// TODO: 포맷에 맞게 숫자 보여주기, 현재는 dd일 hh:mm:ss 고정
export const getFormattedTimeLeft = (msLeft: number, _format: string) => {
  if (msLeft <= 0) {
    return `00일 00:00:00`;
  }

  const secLeft = Math.floor(msLeft / 1000);
  const days = Math.floor(secLeft / 86400)
    .toString()
    .padStart(2, "0");
  const hours = Math.floor((secLeft % 86400) / 3600)
    .toString()
    .padStart(2, "0");
  const minutes = Math.floor((secLeft % 3600) / 60)
    .toString()
    .padStart(2, "0");
  const seconds = (secLeft % 60).toString().padStart(2, "0");

  return `${days}일 ${hours}:${minutes}:${seconds}`;
};

export const getCountdownType = (countdown: ITimerCountDown) => {
  const isDynamic =
    countdown.startAt === "DYNAMIC" &&
    typeof countdown.endAt === "object" &&
    countdown.endAt.type === "FROM_NOW";

  return isDynamic ? "DYNAMIC" : "STATIC";
};

export const getCountdownTimeLeft = (countdown: ITimerCountDown) => {
  const countdownType = getCountdownType(countdown);

  // 고정형
  if (countdownType === "STATIC") {
    // 반복 없음
    if (typeof countdown.endAt === "string" && !countdown.repeat) {
      return dayjs(countdown.endAt).diff(dayjs());
    }

    // 매일
    if (countdown.repeat === "DAILY" && typeof countdown.endAt === "object") {
      throw new Error("Not implemented");
      // TODO: 반복형 구현되면 사용
      // return dayjs()
      //   .set("h", countdown.endAt.h)
      //   .set("m", countdown.endAt.m)
      //   .set("s", countdown.endAt.s)
      //   .add(dayjs().utcOffset() + (countdown.utcOffset ?? -540), "m")
      //   .diff(dayjs());
    }

    // 요일마다
    if (
      typeof countdown.repeat === "number" &&
      typeof countdown.endAt === "object"
    ) {
      throw new Error("Not implemented");
      // TODO: 반복형 구현되면 사용
      // return dayjs()
      //   .set("h", countdown.endAt.h)
      //   .set("m", countdown.endAt.m)
      //   .set("s", countdown.endAt.s)
      //   .day(countdown.repeat)
      //   .add(dayjs().utcOffset() + (countdown.utcOffset ?? -540), "m")
      //   .diff(dayjs());
    }
    throw new Error("Not implemented");
  } else {
    throw new Error("Not implemented");
  }
};

export const getLargestTimeUnit = (timeUnits: string[]) => {
  if (timeUnits.includes("d")) {
    return "d";
  }
  if (timeUnits.includes("h")) {
    return "h";
  }
  if (timeUnits.includes("m")) {
    return "m";
  }
  if (timeUnits.includes("s")) {
    return "s";
  }
  return "s";
};

export const getTimeUnitsFromContent = (content: string) => {
  const doubleBracesTimeStringRegex =
    /{{(?:\s|&nbsp;)*(?:time\.)(.*?)(?:\s|&nbsp;)*}}/g;
  const matchedUnits = [...content.matchAll(doubleBracesTimeStringRegex)]
    .map((m) => m[1])
    .filter(isNotFalsy);
  return matchedUnits;
};

export const getTimeLeftByUnits = (msLeft: number, units: string[]) => {
  const timeLefts = {
    d: "00",
    h: "00",
    m: "00",
    s: "00",
  };
  if (msLeft <= 0) {
    return timeLefts;
  }

  const timeUnits = {
    d: false,
    h: false,
    m: false,
    s: false,
  };

  for (const unit of units) {
    if (unit === "d" || unit === "h" || unit === "m" || unit === "s") {
      timeUnits[unit] = true;
    }
  }

  if (timeUnits.d) {
    const d = Math.floor(msLeft / 86400000);
    timeLefts.d = d.toString().padStart(2, "0");
    msLeft -= d * 86400000;
  }
  if (timeUnits.h) {
    const h = Math.floor(msLeft / 3600000);
    timeLefts.h = h.toString().padStart(2, "0");
    msLeft -= h * 3600000;
  }
  if (timeUnits.m) {
    const m = Math.floor(msLeft / 60000);
    timeLefts.m = m.toString().padStart(2, "0");
    msLeft -= m * 60000;
  }
  if (timeUnits.s) {
    const s = Math.floor(msLeft / 1000);
    timeLefts.s = s.toString().padStart(2, "0");
  }

  return timeLefts;
};

export const formatsToContent = (format: string) => {
  switch (format) {
    case "dd일":
      return "<p>{{time.d}}일</p>";
    case "dd일 hh:mm":
      return "<p>{{time.d}}일 {{time.h}}:{{time.m}}</p>";
    case "dd일 hh:mm:ss":
      return "<p>{{time.d}}일 {{time.h}}:{{time.m}}:{{time.s}}</p>";
    case "hh:mm":
      return "<p>{{time.h}}:{{time.m}}</p>";
    case "hh:mm:ss":
      return "<p>{{time.h}}:{{time.m}}:{{time.s}}</p>";
    default:
      return format;
  }
};
